import { useMemo } from 'react'
import { createBackendApi, type BackendApi } from '@healthshare/shared/backend/client'
import { createHttpTransport } from '@healthshare/shared/backend/httpTransport'
import { useSession } from '../providers/supabase'

/**
 * The web app's binding of the generated `BackendApi` (Stage 2 of
 * docs/plans/native-app-sdk-seams.md) -- one `HttpTransport` per access token,
 * origin-relative like `webBackendClient` (Kong fronts both apps on one origin,
 * so `HttpTransport`'s default `/backend` base URL is what makes a path like
 * `/places/search` resolve to `/backend/places/search`, same-origin).
 *
 * Superseds `useBackendAuth().getAuthHeaders()` for call sites that migrate onto
 * a typed `BackendApi` method: the transport attaches `Authorization` itself, so
 * callers no longer spread auth headers into every `fetch` by hand.
 * `useBackendAuth` stays for the handful of call sites this stage doesn't touch
 * (the Plaid Hosted Link bridge, which isn't part of the command surface -- see
 * lib/plaid/hostedLink.ts).
 */
export function useBackendApi(): BackendApi {
  const session = useSession()
  const accessToken = session?.access_token

  return useMemo(
    () => createBackendApi(createHttpTransport({ getAccessToken: () => accessToken })),
    [accessToken],
  )
}
